import React, { useState, useEffect } from 'react';
import '../stylings/BucksAbout.css';
import bucksStoryHeroImage from '../imgs/bucksStoryHeroCover.png';
import { 
  User, 
  Sprout, 
  HeartHandshake, 
  UtensilsCrossed, 
  Tractor, 
  Dog,
  Newspaper,
  Search,
  Shovel,
  Wheat,
  LeafyGreen,
  Flower,
  Sunrise,
  Star,
  ShoppingBasket     // Added for enhanced CTA button
} from 'lucide-react';

function BucksAbout() {
  const [isVisible, setIsVisible] = useState(false);

  // Farmers data with Lucide icons instead of photos
  const farmers = [
    { 
      name: 'Molly Walker', 
      role: 'Owner & Manager', 
      icon: <User size={64} strokeWidth={1.5} />,
      color: '#6b4c35'  
    },
    { 
      name: 'David Walker', 
      role: 'Inventory Manager', 
      icon: <Sprout size={64} strokeWidth={1.5} />,
      color: '#4a7c40'
    },
    { 
      name: 'Joan Walker', 
      role: "Buck's Farmer", 
      icon: <Flower size={64} strokeWidth={1.5} />,  // Changed from Shovel to Flower
      color: '#a67c52'
    },
    { 
      name: 'Jerri Ann', 
      role: "Buck's Farmer", 
      icon: <Wheat size={64} strokeWidth={1.5} />,
      color: '#c48f44'
    },
    { 
      name: 'Azya Walker', 
      role: "Buck's Farmer", 
      icon: <LeafyGreen size={64} strokeWidth={1.5} />,
      color: '#556a4b'
    },
    { 
      name: 'Quinn Walker', 
      role: "Buck's Farmer", 
      icon: <LeafyGreen size={64} strokeWidth={1.5} />,
      color: '#3d5a7d'
    },
    { 
      name: 'Charlie', 
      role: 'Forever in Our Hearts', 
      icon: <Star size={64} strokeWidth={1.5} />,  // Changed from Dog to Star
      color: '#d4af37'  // Changed to gold color for remembrance
    }
  ];

  // Animation on mount
  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div className="bucks-about">
      {/* About Hero Section */}
      <section className={`about-hero ${isVisible ? 'visible' : ''}`}>
        <div className="about-hero-content">
          <h1>Our Story</h1>
          <p className="about-hero-subtitle">Nearly half a century of farm-fresh tradition</p>
        </div>
      </section>

      {/* Heritage Section */}
      <section className="heritage-section">
        <div className="heritage-container">
          <div className="heritage-content">
            <div className="heritage-badge">Est. 1975</div>
            <h2>A Legacy of Fresh Produce</h2>
            <p className="heritage-intro">Buck's Farmstand was first established by Buck Walker almost 50 years ago and has been passed down to his grandsons, and most recently his great-grandaughter, throughout the years.</p>
            <p>The farmstand remains in operation today with the same Buck's charm, welcoming spirit, and dedication to having farm fresh produce delivered daily as it did when it was first established.</p>
            <p>If you happen to stop by Buck's today, you'll not only be greeted with a warm smile, friendly customer service, and affordable pricing, but also the same memorable local experience created by Buck all those years ago.</p>
          </div>
          <div className="heritage-image">
            <img src={bucksStoryHeroImage} alt="Buck's Farmstand historical view" />
            <div className="image-caption">Buck's Farmstand, circa 1995</div>
          </div>
        </div>
      </section>

      {/* Press Feature Section - UPDATED WITH LINKS */}
      <section className="press-feature">
        <div className="press-container">
          <div className="press-badge">
            <span>As Featured In</span>
          </div>
          <h2 className="press-title">Chicago Tribune</h2>
          <div className="press-quote">
            <blockquote>
              "Buck's Farmstand remains one of Chicago's hidden treasures, offering the kind of genuine farm-to-table experience that has become increasingly rare in today's world."
            </blockquote>
            <div className="quote-source">— Chicago Tribune, 1995</div>
            
            <div className="press-links">
              <a href="https://www.chicagotribune.com/1995/09/10/its-so-sweet/" target="_blank" rel="noopener noreferrer" className="press-link">
                <div className="press-link-icon">
                  <Newspaper size={32} color="#a67c52" />
                </div>
                <div className="press-link-details">
                  <span className="press-link-title">"It's So Sweet"</span>
                  <span className="press-link-source">Chicago Tribune, September 10, 1995</span>
                </div>
              </a>
              
              <a href="https://archive.ph/uqOEI" target="_blank" rel="noopener noreferrer" className="press-link">
                <div className="press-link-icon">
                  <Search size={32} color="#a67c52" />
                </div>
                <div className="press-link-details">
                  <span className="press-link-title">Archived Feature</span>
                  <span className="press-link-source">Complete Article Archive</span>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Meet the Farmers Section - UPDATED WITH LUCIDE ICONS */}
      <section className="farmers-section">
        <div className="farmers-container">
          <h2>Meet Our Buck's Farmers</h2>
          <p className="farmers-intro">The dedicated people who bring fresh produce to your table every day.</p>
          <div className="farmers-grid">
            {farmers.map((farmer, index) => (
              <div className={`farmer-card ${farmer.name === 'Charlie' ? 'memorial-card' : ''}`} key={index}>
                <div className="farmer-icon-container">
                  <div className="farmer-icon" style={{ color: farmer.color }}>
                    {farmer.icon}
                  </div>
                </div>
                <h3 className="farmer-name">{farmer.name}</h3>
                <p className="farmer-role">{farmer.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hours & Location Section */}
      <section className="hours-location">
        <div className="hours-location-container">
          <div className="hours-container">
            <h2>Farmstand Hours</h2>
            <div className="hours-grid">
              <div className="day">Monday - Friday</div>
              <div className="time">9AM – 4:00PM</div>
              <div className="day">Saturday</div>
              <div className="time">9AM – 1PM</div>
              <div className="day">Sunday</div>
              <div className="time">Closed</div>
            </div>
          </div>
          <div className="location-container">
            <h2>Our Location</h2>
            <div className="map-container">
              <iframe 
                title="Buck's Farmstand Location"
                width="100%" 
                height="100%" 
                frameBorder="0" 
                scrolling="no" 
                marginHeight="0" 
                marginWidth="0" 
                src="https://maps.google.com/maps?q=4250+W+123rd+St,+Alsip,+IL+60803&z=15&output=embed"
              ></iframe>
            </div>
            <div className="address">
              <p>Come Visit Bucks!</p>
              <p>4250 W 123rd St, Alsip, IL 60803</p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section - ENHANCED SHOP BUTTON */}
      <section className="about-cta">
        <div className="cta-container">
          <h2>Experience the Buck's Difference</h2>
          <p>We look forward to welcoming you to our farmstand and sharing our passion for fresh, local produce.</p>
          <div className="cta-buttons">
            <a href="/shop" className="cta-primary enhanced-cta">
              <span className="cta-icon"><ShoppingBasket size={18} /></span>
              <span>Shop Our Products</span>
            </a>
            <a href="/contact" className="cta-secondary">Contact Us</a>
          </div>
        </div>
      </section>

      {/* Additional CSS for press links and farmer icons */}
      <style>
        {`
          .press-links {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 1.5rem;
            margin-top: 2.5rem;
          }
          
          .press-link {
            display: flex;
            align-items: center;
            background-color: white;
            border-radius: 12px;
            padding: 1rem 1.5rem;
            text-decoration: none;
            color: inherit;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
            transition: all 0.3s ease;
            max-width: 320px;
            width: 100%;
          }
          
          .press-link:hover {
            transform: translateY(-5px);
            box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
            background-color: #fffbf2;
          }
          
          .press-link-icon {
            margin-right: 1rem;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          
          .press-link-details {
            display: flex;
            flex-direction: column;
          }
          
          .press-link-title {
            font-weight: 600;
            color: #6b4c35;
            font-size: 1rem;
            margin-bottom: 0.3rem;
          }
          
          .press-link-source {
            font-size: 0.85rem;
            color: #a67c52;
          }
          
          /* Farmer icon styling */
          .farmer-icon-container {
            width: 100%;
            height: 160px;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: #f9f4e8;
            position: relative;
            overflow: hidden;
          }
          
          .farmer-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 120px;
            height: 120px;
            background-color: white;
            border-radius: 50%;
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
            transition: transform 0.5s ease;
          }
          
          .farmer-card:hover .farmer-icon {
            transform: scale(1.1);
          }
          
          /* Memorial card styling */
          .memorial-card {
            position: relative;
            overflow: hidden;
          }
          
          .memorial-card::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            border: 2px solid #d4af37;
            border-radius: 12px;
            opacity: 0.3;
            pointer-events: none;
          }
          
          .memorial-card .farmer-icon {
            box-shadow: 0 4px 20px rgba(212, 175, 55, 0.3);
          }
          
          .memorial-card .farmer-name {
            position: relative;
          }
          
          .memorial-card .farmer-name::after {
            content: '★';
            font-size: 0.8rem;
            display: inline-block;
            margin-left: 5px;
            color: #d4af37;
          }
          
          /* Enhanced CTA button styling */
          .enhanced-cta {
            display: flex;
            align-items: center;
            padding: 0.9rem 2.5rem;
            border-radius: 50px;
            background-color: #ffffff;
            color: #a67c52;
            font-weight: 700;
            font-size: 1.15rem;
            box-shadow: 0 6px 18px rgba(0, 0, 0, 0.15), 0 2px 3px rgba(0, 0, 0, 0.1);
            letter-spacing: 0.3px;
            position: relative;
            overflow: hidden;
            z-index: 1;
            transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
            border: 2px solid rgba(166, 124, 82, 0.1);
          }
          
          .enhanced-cta::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-image: linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(245, 240, 229, 0.5) 100%);
            z-index: -1;
          }
          
          .enhanced-cta:hover {
            transform: translateY(-5px) scale(1.03);
            box-shadow: 0 12px 24px rgba(166, 124, 82, 0.25), 0 4px 8px rgba(0, 0, 0, 0.12);
            color: #8c6544;
            background-color: #fffbf2;
            border-color: rgba(166, 124, 82, 0.3);
          }
          
          .enhanced-cta:active {
            transform: translateY(-2px);
            box-shadow: 0 5px 10px rgba(166, 124, 82, 0.2);
          }
          
          .cta-icon {
            display: flex;
            align-items: center;
            margin-right: 0.8rem;
          }
          
          .about-cta .cta-secondary {
            border: 2px solid white;
          }
          
          .about-cta .cta-secondary:hover {
            background-color: rgba(255, 255, 255, 0.2);
          }
          
          /* Responsive adjustments */
          @media (max-width: 768px) {
            .press-links {
              flex-direction: column;
              align-items: center;
              gap: 1rem;
            }
            
            .press-link {
              max-width: 100%;
            }
            
            .farmer-icon {
              width: 100px;
              height: 100px;
            }
            
            .farmer-icon svg {
              width: 52px;
              height: 52px;
            }
            
            .enhanced-cta {
              padding: 0.8rem 2rem;
            }
          }
        `}
      </style>
    </div>
  );
}

export default BucksAbout;